import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import * as Yup from "yup";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { inAppNotificationKeys } from "../../../../api/queryKeys";
import getAppScenes from "../../../../components/structure/Navigation";
import iconMapping from "../../../../icons/IconMapping";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { NotificationType } from "../../../../utils/constants";
import { IntegrationsMapping } from "../../../stack/integrations/IntegrationsMapping";

const AddNotificationModal = ({ open, handleClose }) => {
  const { createAppManualInAppNotification, fetchAllUsersMail } = useAPI();
  const [emails, setEmails] = useState({});
  const queryClient = useQueryClient();
  const { data: userEmails } = useQuery({
    queryKey: ["all_users_mail"],
    queryFn: fetchAllUsersMail,
    select: (data) => [...new Set(data?.data)],
    enabled: open,
  });

  useEffect(() => {
    if (userEmails) {
      setEmails(
        userEmails?.reduce((acc, email) => {
          acc[email] = false;
          return acc;
        }, {})
      );
    }
  }, [userEmails]);

  const colors = useColors();
  const validationSchema = Yup.object({
    user_emails: Yup.array()
      .of(Yup.string().email("Enter a valid email"))
      .required("User email is required"),
    title: Yup.string().required("Required"),
    description: Yup.string().required("Required"),
    link: Yup.string().required("Required"),
    notification_type: Yup.string().required("Required"),
    icon_name: Yup.string(),
    label: Yup.string(),
  });

  const initialValues = {
    user_emails: [],
    title: "",
    description: "",
    link: "",
    notification_type: "",
    icon_name: "",
    label: "",
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await createAppManualInAppNotification(values).then(() => {
        handleClose();
        queryClient.invalidateQueries({ queryKey: inAppNotificationKeys.all });
      });
    } catch (error) {
      console.error("Error adding notification:", error);
    } finally {
      //    setSubmitting(false);
    }
  };
  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 700,
    maxHeight: 700,
    bgcolor: colors.common_white,
    borderRadius: 2,
    p: 3,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  };

  const notificationTypes = [...Object.values(NotificationType), "Other"];
  const iconOptions = Object.keys(iconMapping);
  const toolsIconsOptions = Object.keys(IntegrationsMapping);
  const notificationTypesIcons = (notificationType) => {
    if (notificationType === NotificationType.HIGH_RISK_GAP)
      return toolsIconsOptions;
    else return iconOptions;
  };
  const linkOptions = getAppScenes()
    .map((scene) => {
      let path = [];

      if (scene.pages) {
        scene.pages.forEach((page) => {
          path.push(page.path);
        });
      }
      return path;
    })
    .flat();

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{ ...modalStyle }}>
        <Typography variant="h6" component="h2">
          Add Notification
        </Typography>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            isSubmitting,
          }) => (
            <Form
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                overflowY: "auto",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: "8px 16px",
                  overflowY: "auto",
                }}
              >
                <FormControl
                  fullWidth
                  margin="normal"
                  error={touched.user_emails && Boolean(errors.user_emails)}
                >
                  <Autocomplete
                    multiple
                    id="user_emails"
                    options={userEmails || []}
                    value={values.user_emails}
                    onChange={(event, newValue) => {
                      setFieldValue("user_emails", newValue);
                    }}
                    onBlur={handleBlur}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          key={option}
                          label={option}
                          {...getTagProps({ index })}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="User Emails"
                        placeholder="Select user emails"
                        error={
                          touched.user_emails && Boolean(errors.user_emails)
                        }
                        helperText={touched.user_emails && errors.user_emails}
                      />
                    )}
                  />
                </FormControl>
                <TextField
                  label="Title"
                  name="title"
                  value={values.title}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.title && Boolean(errors.title)}
                  helperText={touched.title && errors.title}
                  margin="normal"
                />
                <TextField
                  label="Description"
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.description && Boolean(errors.description)}
                  helperText={touched.description && errors.description}
                  margin="normal"
                />

                <Autocomplete
                  id="link"
                  options={linkOptions}
                  value={values.link}
                  onChange={(event, newValue) => {
                    setFieldValue("link", newValue);
                  }}
                  sx={{
                    width: "100%",
                  }}
                  onBlur={handleBlur}
                  freeSolo
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Link"
                      placeholder="Select a link"
                      error={touched.link && Boolean(errors.link)}
                      helperText={touched.link && errors.link}
                      margin="normal"
                      onChange={(e) => {
                        handleChange(e);
                        setFieldValue("link", e.target.value);
                      }}
                    />
                  )}
                />
                <FormControl
                  fullWidth
                  margin="normal"
                  error={
                    touched.notification_type &&
                    Boolean(errors.notification_type)
                  }
                >
                  <InputLabel id="notification-type-label">
                    Notification Type
                  </InputLabel>
                  <Select
                    labelId="notification-type-label"
                    id="notification_type"
                    name="notification_type"
                    value={values.notification_type}
                    onChange={(e) => {
                      handleChange(e);
                      setFieldValue("icon_name", "");
                    }}
                    onBlur={handleBlur}
                    label="Notification Type"
                  >
                    {notificationTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.notification_type && errors.notification_type && (
                    <FormHelperText>{errors.notification_type}</FormHelperText>
                  )}
                </FormControl>
                <Autocomplete
                  id="icon_name"
                  options={notificationTypesIcons(values.notification_type)}
                  value={values.icon_name}
                  onChange={(event, newValue) => {
                    setFieldValue("icon_name", newValue);
                  }}
                  onBlur={handleBlur}
                  sx={{
                    width: "100%",
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Icon Footer"
                      placeholder="Select an icon"
                      error={touched.icon_name && Boolean(errors.icon_name)}
                      helperText={touched.icon_name && errors.icon_name}
                      margin="normal"
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {values.notification_type !==
                        NotificationType.HIGH_RISK_GAP && (
                        <SvgToIcon
                          IconLogo={iconMapping[option]}
                          style={{
                            width: 32,
                            height: 32,
                            mr: 1,
                          }}
                        />
                      )}
                      {option}
                    </li>
                  )}
                />
                <TextField
                  label="Label Footer"
                  name="label"
                  value={values.entity_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.entity_name && Boolean(errors.entity_name)}
                  helperText={touched.entity_name && errors.entity_name}
                  margin="normal"
                />
              </Box>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
              >
                Add Notification
              </Button>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default AddNotificationModal;
